import * as React from 'react';
import {withRouter, Link as RouterLink} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {Routing} from 'nutshell-core/routing';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {CampaignIcon} from 'shells/icon/svg/campaign-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EnvelopeIcon} from 'shells/icon/svg/envelope-icon';
import {WarningIcon} from 'shells/icon/svg/warning-icon';
import {Link} from 'shells/link/link';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import {getClickTooltipText, getReplyTooltipText} from './helpers';
import type {TemplateItem} from './template-list';

import styles from './templates.module.css';

type OwnProps = {
    showCreator: boolean;
    template: TemplateItem;
    deleteSequenceTemplate: (templateId: string) => Promise<any>;
    undeleteSequenceTemplate: (templateId: string) => Promise<any>;
    onSequenceRename: (templateId: string, currentName: string) => void;
};

type Props = ContextRouter & OwnProps;

class TemplateListRowComponentComponent extends React.Component<Props> {
    componentDidMount() {
        ReactTooltip.rebuild();
    }

    render() {
        const {template, showCreator} = this.props;
        const replyRate =
            typeof template.replyRate === 'number' ? `${template.replyRate.toFixed(0)}%` : '--';
        const clickRate =
            typeof template.clickRate === 'number' ? `${template.clickRate.toFixed(0)}%` : '--';

        let options = [
            {
                label: 'Edit',
                handleSelect: this.handleEdit,
            },
            {
                label: 'Rename',
                handleSelect: this.handleRename,
            },
            {
                label: 'View people recipients',
                variant: 'primary',
                handleSelect: this.handleViewPeople,
            },
            {
                label: 'View lead recipients',
                variant: 'primary',
                handleSelect: this.handleViewLeads,
            },
            {
                label: 'Archive',
                variant: 'destructive',
                handleSelect: this.handleArchive,
            },
        ];

        // Template is shared by someone else
        if (template.isImmutable) {
            options = [
                {
                    label: 'View',
                    handleSelect: this.handleEdit,
                },
            ];

            if (template.canEdit) {
                options.push({
                    label: 'Archive',
                    variant: 'destructive',
                    handleSelect: this.handleArchive,
                });
            }
        }

        if (template.isArchived) {
            options = [
                {
                    label: 'Restore',
                    handleSelect: this.handleRestore,
                },
            ];
        }

        const replyTooltip = getReplyTooltipText(template);
        const clickTooltip = getClickTooltipText(template);

        return (
            <tr className={template.isArchived ? styles['row--archived'] : styles.row}>
                <th scope='row'>
                    <div className={styles['row-name-cell']}>
                        <div>
                            <RowTitle template={template} />
                            <RowSummary template={template} showCreator={showCreator} />
                        </div>
                    </div>
                </th>
                <td>
                    {template.lastSentTime ? (
                        <RelativeTimestamp datetime={template.lastSentTime} />
                    ) : (
                        'Never'
                    )}
                </td>
                <td>
                    <div className={styles['right-aligned-cell-container']}>
                        <div data-tip={replyTooltip} className={styles['right-aligned-cell']}>
                            {replyRate}
                        </div>
                    </div>
                </td>
                <td>
                    <div className={styles['right-aligned-cell-container']}>
                        <div data-tip={clickTooltip} className={styles['right-aligned-cell']}>
                            {clickRate}
                        </div>
                    </div>
                </td>
                <td>
                    <div className={styles['action-menu']}>
                        {/* @ts-expect-error */}
                        <ActionMenu options={options} />
                    </div>
                </td>
            </tr>
        );
    }

    handleEdit = () => {
        this.props.history.push(getEditLink(this.props.template));
    };

    handleRename = () => {
        this.props.onSequenceRename(this.props.template.id, this.props.template.name);
    };

    handleViewPeople = () => {
        // @ts-expect-error
        window.location = `/people?${this.getSerializedFilter()}&persist=false`;
    };

    handleViewLeads = () => {
        // @ts-expect-error
        window.location = `/leads?${this.getSerializedFilter()}&persist=false`;
    };

    handleArchive = () => {
        this.props.deleteSequenceTemplate(this.props.template.id);
    };

    handleRestore = () => {
        this.props.undeleteSequenceTemplate(this.props.template.id);
    };

    getSerializedFilter = () => {
        return Routing.param({
            filter: {
                receivedEmailSequenceTemplates: {
                    data: [
                        {
                            data: this.props.template.id,
                        },
                    ],
                },
            },
        });
    };
}

function getEditLink(template: TemplateItem) {
    return `${location.pathname.replace(/\/$/, '')}/edit?sequenceId=${template.id}`;
}

// @ts-expect-error
export const TemplateListRowComponent = withRouter<OwnProps>(TemplateListRowComponentComponent);

type RowTitleProps = {
    template: TemplateItem;
};

function RowTitle(props: RowTitleProps) {
    const {template} = props;

    return (
        <div>
            {template.emailAutomation && !template.userChildSequenceTemplate ? (
                // @ts-expect-error
                <WarningIcon color={colors.yellow} size={13} data-tip='Customize your version' />
            ) : undefined}
            {template.isArchived ? (
                template.name
            ) : (
                // @ts-expect-error
                <Link as={RouterLink} variant='primary' to={getEditLink(template)}>
                    {template.name}
                </Link>
            )}
        </div>
    );
}

type RowSummaryProps = {
    template: TemplateItem;
    showCreator: boolean;
};

function RowSummary(props: RowSummaryProps) {
    const {template, showCreator} = props;

    const numMessages = template.emailSequenceTemplateMessages.length;
    const Icon = numMessages > 1 ? CampaignIcon : EnvelopeIcon;
    const infoDescription = getMessageDescription(template, showCreator);

    return (
        <div className={styles['row-summary']}>
            <Icon size={15} />
            <div className={styles['row-summary-text']}>{infoDescription}</div>
        </div>
    );
}

function getMessageDescription(template: TemplateItem, showCreator: boolean): string {
    const numMessages = template.emailSequenceTemplateMessages.length;
    const emailNoun = numMessages === 1 ? 'email' : 'emails';
    const pipelineName = template.emailAutomation
        ? template.emailAutomation.entersStage.stageset.name
        : null;

    let messageDescription = `${numMessages} ${emailNoun}`;
    if (showCreator && template.creatorUser) {
        messageDescription += ` · Shared by ${template.creatorUser.name}`;
    } else if (template.isShared) {
        messageDescription += ` · Shared with your company`;
    }

    if (pipelineName) {
        messageDescription += ` · Automatically sent in ${pipelineName}`;
    }

    return messageDescription;
}
